import React, { CSSProperties, useCallback, useMemo } from 'react';
import { Popover, Spin, TooltipProps } from 'antd';
import {
  rtkQueryPatchStatusToConfigurationFieldStatus,
  SearchableSelectOptionType,
} from '@ydistri/utils';
import { ConfigurationSelect } from '@ydistri/ds';
import { styled } from 'styled-components';
import useIsRootCategory from '../../../../hooks/useIsRootCategory';
import { EntityListResponse, EntityListTypeName } from '@ydistri/api-sdk';
import {
  getDefaultEntityListsParams,
  useGetEntityListsQuery,
} from '../../../ProjectAdministration/EntityListAdministration/apiEntityLists';
import { computeRemSize } from '@ydistri/theme';
import { useTemplateOrCalculationWithValue } from '../../../../hooks/useTemplateOrCalculationWithValue';
import { skipToken } from '@reduxjs/toolkit/query';
import DataDeletedEntityListIcon from '../../../../components/domain/EntityLists/DataDeletedEntityListIcon';
import ArchivedEntityListIcon from '../../../../components/domain/EntityLists/ArchivedEntityListIcon';
import {
  useGetCalculationConfigurationQuery,
  useUpdateCalculationConfigurationMutation,
} from '../../../../apis/apiGlobalConfiguration';

const SelectWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

interface ConfigurationSelectRequestListProps {
  disabled?: boolean;
  width?: CSSProperties['width'];
}

const OptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;
`;

const OptionRequestListName = styled.div`
  width: ${computeRemSize(225)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OptionRequestListItemCount = styled.div`
  font-style: italic;
`;

const requestListState = (entityListResponse: EntityListResponse): React.ReactNode => {
  if (entityListResponse.isDataDeleted) return <DataDeletedEntityListIcon />;
  return entityListResponse.isArchived ? <ArchivedEntityListIcon /> : '';
};

const requestListSelectOptionLabel = (
  entityListResponse: EntityListResponse,
): SearchableSelectOptionType['label'] => (
  <OptionWrapper>
    <OptionRequestListName>
      {requestListState(entityListResponse)}
      {entityListResponse.name ?? '-'}
    </OptionRequestListName>
    <OptionRequestListItemCount>
      ({entityListResponse.skuCount > 0 ? `${entityListResponse.skuCount} items` : `empty`})
    </OptionRequestListItemCount>
  </OptionWrapper>
);

const ConfigurationSelectRequestList: React.FC<ConfigurationSelectRequestListProps> = ({
  disabled = false,
  width,
}) => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const rootCategory = useIsRootCategory();

  // TODO: will be completely changed to work with request lists that are entity lists
  const { data: requestLists, isFetching: requestListsFetching } = useGetEntityListsQuery(
    getDefaultEntityListsParams(EntityListTypeName.Request),
  );

  const { data: calculationConfiguration, isFetching: calculationConfigurationFetching } =
    useGetCalculationConfigurationQuery(templateOrCalculation.value);
  const [updateCalculationConfiguration, updateCalculationConfigurationStatus] =
    useUpdateCalculationConfigurationMutation();

  const latestStatus = rtkQueryPatchStatusToConfigurationFieldStatus(
    updateCalculationConfigurationStatus,
  );

  const selectedEntityListId = calculationConfiguration?.ptRequestListEntityList?.entityListId;

  const options: SearchableSelectOptionType[] = useMemo(() => {
    if (requestLists) {
      const filtered =
        templateOrCalculation.type === 'Template'
          ? requestLists.filter(t =>
              templateOrCalculation.type === 'Template'
                ? !t.isArchived || t.entityListId === selectedEntityListId
                : true,
            )
          : requestLists;

      return filtered
        .map(t => ({
          value: t.entityListId,
          label: requestListSelectOptionLabel(t),
          searchableString: t.name ?? '-',
        }))
        .concat([
          {
            value: 0,
            label: <span> == None ==</span>,
            searchableString: ' == None ==',
          },
        ]);
    }
    return [];
  }, [requestLists, templateOrCalculation.type, selectedEntityListId]);

  const updateRequestList = useCallback(
    (requestListId: number | null) => {
      if (templateOrCalculation.hasValidValue && templateOrCalculation.value !== skipToken) {
        updateCalculationConfiguration({
          templateOrCalculation: templateOrCalculation.value,
          request: {
            ptRequestListEntityListId: requestListId === 0 ? null : requestListId,
          },
        });
      }
    },
    [templateOrCalculation, updateCalculationConfiguration],
  );

  const selectedRequestListId = calculationConfiguration?.ptRequestListEntityList?.entityListId;

  const isLoading = requestListsFetching || calculationConfigurationFetching;

  const tooltipTrigger: TooltipProps['trigger'] = useMemo(() => {
    if (!rootCategory) return ['hover'];
    return [];
  }, [rootCategory]);

  return (
    <Spin spinning={isLoading}>
      <SelectWrapper>
        <Popover content="Editable only in root category" trigger={tooltipTrigger}>
          <div>
            <ConfigurationSelect<number>
              value={selectedRequestListId ?? 0}
              onChange={updateRequestList}
              status={latestStatus}
              options={options}
              showArrow={false}
              disabled={disabled || !rootCategory}
              width={width}
              popupMatchSelectWidth={false}
            />
          </div>
        </Popover>
      </SelectWrapper>
    </Spin>
  );
};

export default ConfigurationSelectRequestList;
